import { InputText } from "primereact/inputtext"
import { Button } from "primereact/button"
import { AutoComplete, AutoCompleteCompleteMethodParams, AutoCompleteSelectParams } from "primereact/autocomplete"
import commonStyles from "../../common.module.css"
import { ChangeEvent, useEffect, useState } from "react"
import { useUNCodesSearch } from "../../../api/queries/useUNCodesSearch"
import { OrderGoodsADR, UNCode } from "../../../api/types/order"
import { InputNumber, InputNumberChangeParams } from "primereact/inputnumber"
import FieldError, { FormErrors } from "../../FieldError"
import { classNames } from "primereact/utils"
import { useOrderDetail } from "../../../api/queries/useOrderDetail"
import { useParams } from "react-router-dom"
import { OrderParams } from "."

export default function OrderFormGoodsItemADR({
  item,
  index,
  ADRChangeHandler,
  ADRRemoveHandler,
  errors,
  canRemove,
  isLast,
}: Props) {
  const { uuid } = useParams<OrderParams>()
  const { data } = useOrderDetail(uuid)
  const orderDetail = data?.results.orderDetail
  const isForeignCountry = orderDetail?.loading?.country !== "CZ" || orderDetail?.unloading?.country !== "CZ"

  const [selected, setSelected] = useState<boolean>(true)
  const [UNCodeQuery, setUNCodeQuery] = useState<string>("")
  const [UNCodeSuggestions, setUNCodeSuggestions] = useState<UNCode[]>([])
  const [selectedADR, setSelectedADR] = useState<OrderGoodsADR | null>(null)

  const handleUNCodeCompletion = (event: AutoCompleteCompleteMethodParams): void => {
    setUNCodeQuery(event.query)
    setSelected(false)
  }

  const handleUNCodeSelection = (event: AutoCompleteSelectParams): void => {
    ADRChangeHandler(Object.assign({}, item, {
      codeUN: event.value.code,
      idAdr: event.value.idAdr,
      description: event.value.description,
    }), index)
    setSelected(true)
  }

  const tUNItem = (item: UNCode): string => `${item.code} - ${item.description}`

  const { data: UNCodes } = useUNCodesSearch({ code: UNCodeQuery, search: !selected })

  useEffect(() => {
    if (UNCodes?.results.UNCodeList) {
      setUNCodeSuggestions(UNCodes.results.UNCodeList)
    }
  }, [UNCodes])

  useEffect(() => {
    if (!selectedADR || selectedADR.idAdr !== item.idAdr) {
      setSelectedADR(item)
      setUNCodeQuery(item.codeUN ? `${item.codeUN} - ${item.description}` : "")
    }
  }, [UNCodeQuery, item, selectedADR])

  const handlePiecesChange = (event: InputNumberChangeParams): void => {
    ADRChangeHandler(Object.assign({}, item, { pieces: event.value }), index)
  }

  const handleBruttoChange = (event: InputNumberChangeParams): void => {
    ADRChangeHandler(Object.assign({}, item, { brutto: event.value }), index)
  }

  const handleNettoChange = (event: InputNumberChangeParams): void => {
    ADRChangeHandler(Object.assign({}, item, { netto: event.value }), index)
  }

  const handleTechnicalNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    ADRChangeHandler(Object.assign({}, item, { technicalName: event.target.value }), index)
  }

  const handleTechnicalNameENChange = (event: ChangeEvent<HTMLInputElement>): void => {
    ADRChangeHandler(Object.assign({}, item, { technicalNameEN: event.target.value }), index)
  }

  return (
    <>
      <div className={"block-title block-title_green border-round-top px-3 flex align-items-center"}>
        ADR č. {index + 1}

        {canRemove && (
          <Button icon={"pi pi-times"} type={"button"}
            className={"p-button-text p-button-icon-only p-button-danger ml-auto"}
            onClick={() => ADRRemoveHandler(index)}
          />
        )}
      </div>

      <div className={`border-solid border-1 border-300 px-3 pt-5 ${isLast ? "" : "mb-6"}`}>
        <div className={"grid"}>
          <div className={"field col-12"}>
            <span className={"p-float-label"}>
              <AutoComplete value={UNCodeQuery} suggestions={UNCodeSuggestions}
                completeMethod={handleUNCodeCompletion}
                onSelect={handleUNCodeSelection}
                field={"description"} itemTemplate={tUNItem}
                className={classNames({ "p-invalid": !!errors?.codeUN })}
              />
              <label className={commonStyles.labelRequired}>UN kód:</label>
            </span>
            <FieldError error={errors?.codeUN} />
          </div>

          <div className={"field col-12 md:col-4"}>
            <span className={"p-float-label"}>
              <InputNumber value={item.pieces || null} min={1} onChange={handlePiecesChange}
                className={classNames({ "p-invalid": !!errors?.pieces })} />
              <label className={commonStyles.labelRequired}>Počet kusů:</label>
            </span>
            <FieldError error={errors?.pieces} />
          </div>

          <div className={"field col-12 md:col-4"}>
            <span className={"p-float-label"}>
              <InputNumber value={item.brutto || null} min={1} onChange={handleBruttoChange}
                className={classNames({ "p-invalid": !!errors?.brutto })} />
              <label className={commonStyles.labelRequired}>Brutto (kg):</label>
            </span>
            <FieldError error={errors?.brutto} />
          </div>

          <div className={"field col-12 md:col-4"}>
            <span className={"p-float-label"}>
              <InputNumber value={item.netto || null} min={1} onChange={handleNettoChange}
                className={classNames({ "p-invalid": !!errors?.netto })} />
              <label className={commonStyles.labelRequired}>Netto (kg):</label>
            </span>
            <FieldError error={errors?.netto} />
          </div>

          <div className={"field col-12 md:col-4"}>
            <span className={"p-float-label"}>
              <InputText maxLength={50} value={item.technicalName}
                onChange={handleTechnicalNameChange}
                className={classNames({ "p-invalid": !!errors?.technicalName })} />
              <label className={commonStyles.labelRequired}>Technický název:</label>
            </span>
            <FieldError error={errors?.technicalName} />
          </div>

          <div className={"field col-12 md:col-4"}>
            <span className={"p-float-label"}>
              <InputText maxLength={50} value={item.technicalNameEN || ""}
                onChange={handleTechnicalNameENChange}
                className={classNames({ "p-invalid": !!errors?.technicalNameEN })} />
              <label className={isForeignCountry ? commonStyles.labelRequired : ""}>Technický název anglicky:</label>
            </span>
            <FieldError error={errors?.technicalNameEN} />
          </div>
        </div>
      </div>
    </>
  )
}

interface Props {
  item: OrderGoodsADR;
  index: number;
  ADRChangeHandler: (item: OrderGoodsADR, index: number) => void;
  ADRRemoveHandler: (ADRIndex: number) => void;
  errors: FormErrors<OrderGoodsADR>;
  canRemove: boolean;
  isLast: boolean;
}
